import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { UserContext } from '../../App';

const API_URL = process.env.REACT_APP_API_URL;
const accessToken = localStorage.getItem("accessToken");

// --- Inline SVG Icons ---
const iconUser = (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);
const iconSearch = (
    <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
    </svg>
);
const iconAdd = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 5v14m7-7H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const iconEye = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);
const iconEdit = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
);
const iconDelete = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1H9a1 1 0 00-1 1v3m1.5-4.5h5" />
    </svg>
);
const iconClose = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const iconDanger = (
    <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto text-red-500 w-12 h-12 mb-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 9V7a2 2 0 00-2-2h-4a2 2 0 00-2 2v2H5.5a1 1 0 000 2h.5V19a2 2 0 002 2h9a2 2 0 002-2v-8h.5a1 1 0 000-2zM9 7a1 1 0 011-1h4a1 1 0 011 1v2H9z" />
    </svg>
);
const iconSuccess = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
);

// --- Custom Modal Component ---
const CustomModal = ({ isOpen, onClose, children, title }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-70 transition-opacity duration-300 ease-out"
            onClick={onClose}>
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto transform transition-all duration-300 ease-out scale-100"
                onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 -mr-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                        {iconClose}
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

// --- Modals using CustomModal ---
const DeleteModal = ({ isOpen, onClose, user, onConfirmDelete }) => {
    if (!user) return null;
    return (
        <CustomModal isOpen={isOpen} onClose={onClose} title="Confirmation de suppression">
            <div className="text-center">
                {iconDanger}
                <h2 className="text-xl font-bold mb-2 text-gray-800">Supprimer le livreur ?</h2>
                <p className="text-gray-600 mb-6">
                    Êtes-vous sûr de vouloir supprimer <b>{user.firstName} {user.lastName}</b> ?
                    Cette action est irréversible.
                </p>
                <div className="flex justify-center space-x-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onConfirmDelete(user.userId)}
                        className="px-6 py-3 bg-red-500 rounded-lg text-white font-semibold hover:bg-red-600 transition-colors"
                    >
                        Confirmer la suppression
                    </button>
                </div>
            </div>
        </CustomModal>
    );
};

const FormModal = ({ isOpen, onClose, onSuccess, userToEdit, currentSupplierId }) => {
    const isEditing = !!userToEdit;
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        address: '',
        city: '',
        password: '',
        userIdBelongTo: currentSupplierId || ''
    });
    const [formError, setFormError] = useState(null);
    const [loading, setLoading] = useState(false);
    const cities = ['Kolwezi', 'Lubumbashi', 'Kinshasa'];
    useEffect(() => {
        if (isEditing && userToEdit) {
            setFormData({
                firstName: userToEdit.firstName || '',
                lastName: userToEdit.lastName || '',
                email: userToEdit.email || '',
                phoneNumber: userToEdit.phoneNumber || '',
                address: userToEdit.address || '',
                city: userToEdit.city || '',
                password: '',
                userIdBelongTo: currentSupplierId || ''
            });
        } else {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                address: '',
                city: '',
                password: '',
                userIdBelongTo: currentSupplierId || ''
            });
        }
    }, [isEditing, userToEdit, currentSupplierId]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFormError(null);

        const payload = { ...formData };
        if (!isEditing) {
            payload.userTypeId = 4;
            payload.password = '12345678'; 
            payload.userIdBelongTo = currentSupplierId;
        }

        try {
            const endpoint = isEditing ? `${API_URL}/edit-user/${userToEdit.userId}` : `${API_URL}/post-user`;
            const method = isEditing ? 'PUT' : 'POST';

            const res = await fetch(endpoint, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || `Échec de ${isEditing ? 'la mise à jour' : "l'ajout"} du livreur.`);
            }

            const data = await res.json();
            onSuccess(data);
            onClose();

        } catch (err) {
            setFormError(err.message || 'Une erreur est survenue.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <CustomModal isOpen={isOpen} onClose={onClose} title={isEditing ? "Modifier le livreur" : "Ajouter un livreur"}>
            {formError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-lg" role="alert">
                    <p className="font-bold">Erreur</p>
                    <p>{formError}</p>
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="firstName">Prénom</label>
                        <input
                            type="text"
                            name="firstName"
                            id="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="Prénom"
                            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            required
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="lastName">Nom de famille</label>
                        <input
                            type="text"
                            name="lastName"
                            id="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Nom de famille"
                            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            required
                        />
                    </div>
                </div>
                <div className="flex flex-col mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="email">Email</label>
                    <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="email@example.com"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        required
                    />
                </div>
                <div className="flex flex-col mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="phoneNumber">Téléphone</label>
                    <input
                        type="tel"
                        name="phoneNumber"
                        id="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        placeholder="+33 6 12 34 56 78"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                </div>

                {/* --- New City Select Field --- */}
                <div className="flex flex-col mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="city">Ville</label>
                    <select
                        name="city"
                        id="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 appearance-none bg-white"
                        required
                    >
                        <option value="" disabled>Sélectionnez une ville</option>
                        {cities.map(city => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                </div>
                {/* ----------------------------- */}

                <div className="flex flex-col mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="address">Adresse</label>
                    <textarea
                        name="address"
                        id="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Adresse postale complète"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        rows="3"
                    />
                </div>
                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                        disabled={loading}
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        className="px-6 py-3 bg-indigo-500 rounded-lg text-white font-semibold hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Ajouter')}
                    </button>
                </div>
            </form>
        </CustomModal>
    );
};

const OrdersModal = ({ isOpen, onClose, user, orders, isLoading }) => {
    if (!isOpen || !user) return null;

    const statusMap = {
        'pending': 'En attente',
        'in_transit': 'En cours de livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'in_transit':
                return 'bg-blue-100 text-blue-800';
            case 'delivered':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getMonthlyDeliveriesChartData = (orderList) => {
        // Filter for delivered orders
        const deliveredOrders = orderList.filter(order => order.status === 'delivered');

        // Aggregate orders by month
        const monthlyDeliveries = deliveredOrders.reduce((acc, order) => {
            const date = new Date(order.createdAt);
            const monthYear = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
            acc[monthYear] = (acc[monthYear] || 0) + 1;
            return acc;
        }, {});

        // Format data for Recharts
        const chartData = Object.keys(monthlyDeliveries).map(key => ({
            name: key,
            "Livraisons": monthlyDeliveries[key]
        })).sort((a, b) => a.name.localeCompare(b.name));

        return chartData;
    };

    const monthlyDeliveriesData = orders ? getMonthlyDeliveriesChartData(orders) : [];
    return (
        <CustomModal isOpen={isOpen} onClose={onClose} title={`Commandes attribuées à ${user.firstName} ${user.lastName}`}>
            {isLoading ? (
                <div className="text-center py-4 text-gray-500">Chargement des commandes...</div>
            ) : orders && orders.length > 0 ? (
                <div className="space-y-4">
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Livraisons mensuelles</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={monthlyDeliveriesData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tickFormatter={(tick) => tick.slice(5)} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="Livraisons" fill="#6366f1" radius={[10, 10, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    {orders.map(order => (
                        <div key={order.orderId} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                            {/* Order Summary */}
                            <div className="flex justify-between items-center mb-2">
                                <h5 className="font-bold text-md text-gray-700">Commande #{order.orderId}</h5>
                                <span className={`inline-block px-3 py-1 mt-1 text-xs font-bold rounded-full ${getStatusStyle(order.status)}`}>
                                    {statusMap[order.status]}
                                </span>
                            </div>

                            {/* Client & Delivery Info */}
                            <div className="text-sm text-gray-600 mb-4">
                                <p>
                                    Client: <span className="font-semibold">{order.clientFirstName} {order.clientLastName}</span>
                                </p>
                                <p>
                                    Téléphone: <span className="font-semibold">{order.clientPhoneNumber || 'N/A'}</span>
                                </p>
                                <p>
                                    Email: <span className="font-semibold">{order.clientEmail || 'N/A'}</span>
                                </p>
                                <p>
                                    Adresse de livraison: {order.clientAddress}
                                </p>
                            </div>

                            {/* Order Items List */}
                            <h6 className="font-bold text-gray-800 mb-2">Détails de la commande</h6>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 mb-4">
                                {order.orderItems.map((item, index) => {
                                    const price = parseFloat(item.priceAtTimeOfOrder);
                                    const formattedPrice = isNaN(price) ? 'N/A' : price.toFixed(2);
                                    return (
                                        <li key={index}>
                                            {item.quantity} x {item.productName} ({formattedPrice} CDF/ {item.unit})
                                        </li>
                                    );
                                })}
                            </ul>

                            {/* Total Amount & Map */}
                            <p className="text-lg font-bold text-gray-900 mt-2 mb-4">
                                Montant Total: {parseFloat(order.totalAmount).toFixed(2)} CDF
                            </p>
                            {order.clientAddress && (
                                <div className="mt-4 rounded-xl overflow-hidden shadow-md">
                                    <iframe
                                        width="100%"
                                        height="200"
                                        frameBorder="0"
                                        scrolling="no"
                                        marginHeight="0"
                                        marginWidth="0"
                                        src={`https://maps.google.com/maps?q=${encodeURIComponent(order.clientAddress)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                                        title={`Map of ${order.clientAddress}`}
                                    ></iframe>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-4 text-gray-500">
                    Ce livreur n'a pas de commandes attribuées.
                </div>
            )}
        </CustomModal>
    );
};


// --- Main Page Component ---
const DeliveryPage = () => {
    const { user } = useContext(UserContext);
    const [inputTerm, setInputTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUserOrders, setSelectedUserOrders] = useState([]);
    const [isOrdersLoading, setIsOrdersLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusMessage, setStatusMessage] = useState(null);
    const [messageType, setMessageType] = useState('success');

    const [deliveryUsers, setDeliveryUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const usersPerPage = 10;

    const showMessage = useCallback((message, type = 'success') => {
        setStatusMessage(message);
        setMessageType(type);
        setTimeout(() => setStatusMessage(null), 5000);
    }, []);

    const fetchDeliveryUsers = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        try {
            const query = new URLSearchParams({
                q: searchTerm,
                page: page,
                limit: usersPerPage,
                supplier_id: user.userId // Filter by supplier ID
            }).toString();

            const res = await fetch(`${API_URL}/delivery-users?${query}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!res.ok) {
                throw new Error("Échec de la récupération des livreurs.");
            }

            const data = await res.json();
            // Additional client-side filtering to ensure only supplier's delivery users
            const filteredUsers = data.deliveryUsers.filter(u => u.userIdBelongTo === user.userId);
            setDeliveryUsers(filteredUsers);
            setTotalCount(filteredUsers.length);
        } catch (err) {
            console.error("Erreur de l'API:", err);
            showMessage("Une erreur est survenue lors du chargement des données.", 'error');
        } finally {
            setLoading(false);
        }
    }, [user, searchTerm, page, showMessage]);

    const fetchUserOrders = useCallback(async (userId) => {
        setIsOrdersLoading(true);
        try {
            const res = await fetch(`${API_URL}/delivery-users/${userId}/orders`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!res.ok) {
                throw new Error("Échec de la récupération des commandes.");
            }

            const data = await res.json();
            setSelectedUserOrders(data.orders);
        } catch (err) {
            console.error("Erreur de l'API:", err);
            showMessage("Une erreur est survenue lors du chargement des commandes.", 'error');
            setSelectedUserOrders([]);
        } finally {
            setIsOrdersLoading(false);
        }
    }, [showMessage]);

    useEffect(() => {
        fetchDeliveryUsers();
    }, [fetchDeliveryUsers, refreshKey]);

    const totalPages = Math.ceil(totalCount / usersPerPage);

    const handleInputChange = (e) => {
        setInputTerm(e.target.value);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(inputTerm);
        setPage(1);
    };

    const handleSuccess = () => {
        setRefreshKey(prev => prev + 1);
        showMessage(`Livreur a été mis à jour avec succès!`);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setIsEditModalOpen(true);
    };

    const handleDeleteUser = (user) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async (userId) => {
        try {
            const res = await fetch(`${API_URL}/delete-user/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Échec de la suppression du livreur.");
            }

            setIsDeleteModalOpen(false);
            setRefreshKey(prev => prev + 1);
            showMessage("Livreur supprimé avec succès !");

        } catch (err) {
            console.error("Erreur lors de la suppression:", err);
            showMessage(err.message || "Une erreur est survenue lors de la suppression.", 'error');
        }
    };

    // New handlers for the orders modal
    const handleOpenOrdersModal = async (user) => {
        setSelectedUser(user);
        await fetchUserOrders(user.userId);
        setIsOrdersModalOpen(true);
    };

    const handleCloseOrdersModal = () => {
        setSelectedUser(null);
        setSelectedUserOrders([]);
        setIsOrdersModalOpen(false);
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-2xl text-gray-700 animate-pulse">Chargement des données...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-extrabold text-gray-900">
                            Gestion des livreurs 🚚
                        </h1>
                        <p className="mt-2 text-lg text-gray-500">
                            Visualisez et recherchez les livreurs de votre plateforme.
                        </p>
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center space-x-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors"
                    >
                        {iconAdd}
                        <span>Ajouter un livreur</span>
                    </button>
                </header>

                {statusMessage && (
                    <div className={`border px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2 ${messageType === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'}`} role="alert">
                        {messageType === 'success' ? iconSuccess : null}
                        <span>{statusMessage}</span>
                    </div>
                )}

                <div className="bg-white bg-opacity-80 backdrop-filter backdrop-blur-lg p-6 rounded-xl shadow-lg mb-6">
                    <form onSubmit={handleSearch}>
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="relative flex-1">
                                {iconSearch}
                                <input
                                    type="text"
                                    value={inputTerm}
                                    onChange={handleInputChange}
                                    placeholder="Rechercher par prénom, nom ou email..."
                                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200"
                                />
                            </div>
                            <button
                                type="submit"
                                className="px-8 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors"
                            >
                                Rechercher
                            </button>
                        </div>
                    </form>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm font-semibold">
                                    <th className="py-4 px-6 text-left rounded-tl-xl">Nom complet</th>
                                    <th className="py-4 px-6 text-left">Email</th>
                                    <th className="py-4 px-6 text-left">Téléphone</th>
                                    <th className="py-4 px-6 text-left">Commandes</th>
                                    <th className="py-4 px-6 text-left">Date d'embauche</th>
                                    <th className="py-4 px-6 text-center rounded-tr-xl">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 text-sm font-light divide-y divide-gray-200">
                                {deliveryUsers.length > 0 ? (
                                    deliveryUsers.map((user) => (
                                        <tr key={user.userId} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-4 px-6 text-left whitespace-nowrap">
                                                <div className="flex items-center space-x-3">
                                                    {iconUser}
                                                    <span className="font-medium text-gray-800">
                                                        {user.firstName || ''} {user.lastName || ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-blue-600 font-medium">
                                                    {user.email || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500">
                                                    {user.phoneNumber || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="font-semibold text-gray-800">
                                                    {user.totalOrders}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-left">
                                                <span className="text-gray-500 text-sm">
                                                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-center">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() => handleOpenOrdersModal(user)}
                                                        className={`p-2 rounded-full text-indigo-500 hover:bg-indigo-100 transition-colors`}
                                                        title="Voir les commandes"
                                                    >
                                                        {iconEye}
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditUser(user)}
                                                        className="p-2 rounded-full text-blue-500 hover:bg-blue-100 transition-colors"
                                                        title="Modifier le livreur"
                                                    >
                                                        {iconEdit}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user)}
                                                        className="p-2 rounded-full text-red-500 hover:bg-red-100 transition-colors"
                                                        title="Supprimer le livreur"
                                                    >
                                                        {iconDelete}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="py-10 text-center text-gray-500">
                                            Aucun livreur trouvé.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-between items-center mt-6">
                        <div className="text-sm text-gray-600">
                            Affichage de {(page - 1) * usersPerPage + 1} à {Math.min(page * usersPerPage, totalCount)} sur {totalCount} livreurs
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <FormModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSuccess={handleSuccess}
                userToEdit={null}
                currentSupplierId={user?.userId}
            />
            <FormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSuccess={handleSuccess}
                userToEdit={selectedUser}
                currentSupplierId={user?.userId}
            />
            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                user={selectedUser}
                onConfirmDelete={handleConfirmDelete}
            />
            <OrdersModal
                isOpen={isOrdersModalOpen}
                onClose={handleCloseOrdersModal}
                user={selectedUser}
                orders={selectedUserOrders}
                isLoading={isOrdersLoading}
            />
        </div>
    );
};

export default DeliveryPage;